import { useCallback, useEffect, useRef, useState } from 'react';
import {
  readDraft as readRaw,
  writeDraft as writeRaw,
  removeDraft as removeRaw,
} from './localStorageDraft';
import useDebouncedDraftWrite from './useDebouncedDraftWrite';

// localStorage-backed draft state for a single checklist section.
// Keys:    route-ops:checklist:<stockingDate>:<sectionId>
// Value:   { fields, completed, updatedAt }
// Writes:  debounced 300ms after the last edit; markComplete and cancel write immediately.
// Cleanup: stale keys (> 2 days old) are dropped once per session by
//          RouteOperationsTab via cleanupStaleChecklistKeys (below).

const STORAGE_PREFIX = 'route-ops:checklist:';
const DEBOUNCE_MS = 300;
const MAX_AGE_DAYS = 2;

function keyFor(stockingDate, sectionId) {
  return `${STORAGE_PREFIX}${stockingDate}:${sectionId}`;
}

function readEntry(stockingDate, sectionId) {
  return readRaw(keyFor(stockingDate, sectionId));
}

function writeEntry(stockingDate, sectionId, fields, completed) {
  writeRaw(
    keyFor(stockingDate, sectionId),
    { fields, completed, updatedAt: new Date().toISOString() },
    'checklist draft write failed',
  );
}

function removeEntry(stockingDate, sectionId) {
  removeRaw(keyFor(stockingDate, sectionId));
}

// Drops checklist keys whose stockingDate component is more than MAX_AGE_DAYS
// behind today's local date. Compares against today-at-midnight so the
// cutoff stays calendar-stable across the day (otherwise a draft created
// yesterday morning would silently disappear by this afternoon).
// Exported for tests.
export function cleanupStaleChecklistKeys(now = new Date()) {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const cutoffMs = midnight.getTime() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  const keysToRemove = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(STORAGE_PREFIX)) continue; // eslint-disable-line no-continue
    const rest = key.slice(STORAGE_PREFIX.length);
    const datePart = rest.split(':')[0];
    const dateMs = Date.parse(datePart);
    if (Number.isNaN(dateMs) || dateMs < cutoffMs) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => window.localStorage.removeItem(k));
}

export default function useDailyChecklistDraft({
  sectionId,
  stockingDate,
  initialFields,
  serverFields,
}) {
  const initialRef = useRef(initialFields || {});
  const [fields, setFieldsState] = useState(initialRef.current);
  const [completed, setCompletedState] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const skipNextWriteRef = useRef(true);

  // Stable signal for the load effect: server data is the source of truth, so
  // a change in its content must re-seed the form. Object identity is unstable
  // across renders, so key off the serialized content instead.
  const serverFieldsKey = serverFields ? JSON.stringify(serverFields) : '';

  // Load from storage (or server) when the key changes (or on mount).
  // Stale-key cleanup is a tab-level concern (RouteOperationsTab), not the
  // per-section hook's — so it isn't triggered here.
  useEffect(() => {
    if (serverFields) {
      // Server is source of truth: seed from server over localStorage, merge
      // defaults under it so any field not present on the server keeps a safe
      // default (avoids controlled→uncontrolled input flips). Stay editable:
      // do NOT set `completed` from the server — gating is handled by the
      // section via a separate `hasServerAction` signal.
      setFieldsState({ ...initialRef.current, ...serverFields });
      setCompletedState(false);
    } else {
      const entry = readEntry(stockingDate, sectionId);
      if (entry) {
        // Merge defaults under stored fields so any keys missing from storage
        // (e.g. fields added in a later release) still have safe defaults.
        // Without this, controlled inputs flip to uncontrolled when their
        // value becomes undefined.
        setFieldsState({ ...initialRef.current, ...(entry.fields || {}) });
        setCompletedState(Boolean(entry.completed));
      } else {
        setFieldsState(initialRef.current);
        setCompletedState(false);
      }
    }
    skipNextWriteRef.current = true;
    // Signal that the load attempt has completed (both the entry-found and
    // no-entry branches). Consumers that seed defaults must wait for this so
    // they observe loaded values before writing.
    setLoaded(true);
  }, [sectionId, stockingDate, serverFieldsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced write whenever fields or completed change post-load, via the
  // shared debounce hook. skipNextWriteRef (init true) suppresses the write on
  // the initial load and after markComplete/cancel, which write immediately.
  const persist = useCallback(() => {
    writeEntry(stockingDate, sectionId, fields, completed);
  }, [fields, completed, stockingDate, sectionId]);

  useDebouncedDraftWrite(persist, {
    debounceMs: DEBOUNCE_MS,
    skipRef: skipNextWriteRef,
  });

  const setField = useCallback((name, value) => {
    setFieldsState((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Mark complete writes immediately (skipping the debounce) so the
  // "completed" flag is durable before any subsequent UI transition.
  const markComplete = useCallback(() => {
    skipNextWriteRef.current = true;
    writeEntry(stockingDate, sectionId, fields, true);
    setCompletedState(true);
  }, [fields, stockingDate, sectionId]);

  // Cancel clears the storage entry AND resets in-memory fields to initial.
  // We deliberately skip the next debounced write so we don't re-create the
  // key we just removed.
  const cancel = useCallback(() => {
    removeEntry(stockingDate, sectionId);
    skipNextWriteRef.current = true;
    setFieldsState(initialRef.current);
    setCompletedState(false);
  }, [stockingDate, sectionId]);

  return { fields, setField, markComplete, cancel, completed, loaded };
}
